import { createContext, useContext, useState, type ReactNode } from 'react';
import { civilDateBounds } from '../civil-date';
import type { WidgetProps } from './types';
import { instantFromWallClock, wallClockInput, zoneLabel } from './zoned-time';

/**
 * Fuseau IANA du formulaire (site, sinon tenant), fourni par le formulaire de gabarit ; `null` : aucun
 * fuseau connu.
 */
export const CivilTimeZoneContext = createContext<string | null>(null);

/**
 * Date et heure `datetime` (ADR-146 §6.1, R1) : l'opérateur tape l'heure du site, la réponse part en
 * instant ISO 8601 UTC. Sans fuseau du site ni du tenant, aucune saisie (jamais l'UTC ni le fuseau du
 * poste par défaut). « Maintenant » remplit l'instant courant.
 */
export function DateTimeInput({ id, field, value, onChange, disabled, invalid, describedBy, today = null, now = () => new Date() }: WidgetProps & { now?: () => Date }): ReactNode {
  const zone = useContext(CivilTimeZoneContext);
  // Saisie en cours qui ne donne pas encore un instant (heure qui n'existe pas ce jour-là).
  const [pending, setPending] = useState<{ text: string; error: string } | null>(null);
  if (field.type !== 'datetime') return null;
  if (zone === null) {
    return (
      <>
        <input id={id} type="datetime-local" disabled aria-invalid={invalid} aria-describedby={describedBy} value="" readOnly />
        <span className="ay-field__error">Fuseau du site inconnu : la date et l’heure ne peuvent pas être saisies ici.</span>
      </>
    );
  }
  const bounds = civilDateBounds(today);
  const shown = pending?.text ?? wallClockInput(value, zone);
  return (
    <span className="ay-datetime">
      <span className="ay-datetime__row">
        <input
          id={id}
          type="datetime-local"
          className="ay-datetime__input"
          required={field.required === true}
          disabled={disabled}
          {...(bounds === null ? {} : { min: `${bounds.min}T00:00`, max: `${bounds.max}T23:59` })}
          aria-invalid={invalid || pending !== null}
          aria-describedby={describedBy}
          value={shown}
          onChange={(event) => {
            const text = event.target.value;
            if (text === '') {
              setPending(null);
              onChange(null);
              return;
            }
            const instant = instantFromWallClock(text, zone);
            if (instant === null) {
              setPending({ text, error: 'Cette heure n’existe pas ce jour-là (changement d’heure).' });
              return;
            }
            setPending(null);
            onChange(instant);
          }}
        />
        <button
          type="button"
          className="ay-button ay-button--secondary ay-datetime__now"
          disabled={disabled}
          onClick={() => {
            setPending(null);
            // À la minute, comme la saisie.
            const instant = Math.floor(now().getTime() / 60_000) * 60_000;
            onChange(new Date(instant).toISOString());
          }}
        >
          Maintenant
        </button>
      </span>
      <span className="ay-datetime__zone">Heure du site : {zoneLabel(zone)}</span>
      {pending !== null && <span className="ay-field__error">{pending.error}</span>}
    </span>
  );
}
